"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  HStack,
  Portal,
  Text,
  VStack,
  Center,
  Circle,
  Image,
} from "@chakra-ui/react";
import { LogOut, ArrowRight } from "lucide-react";
import KleerBadge from "@/components/ui/KleerBadge"; 
import ActionButton from "@/components/ui/ActionButton";

type SessionTimeoutModalProps = {
  isOpen: boolean;
  onContinue: () => void;
  onLogout: () => void;
  remainingSeconds: number;
  closeOnly?: boolean;
};

export default function SessionTimeoutModal({
  isOpen,
  onContinue,
  onLogout,
  remainingSeconds,
  closeOnly = false,
}: SessionTimeoutModalProps) {
  const { t } = useTranslation("translation");
  if (!isOpen) return null;

  // Deux variantes : la modale normale (anglais d'origine) et la variante
  // `closeOnly` des pages Vigilab (français d'origine), chacune avec ses clés.
  const texts = closeOnly
    ? {
        title: t("sessionModal.closeOnly.title", { defaultValue: "Session bientôt expirée" }),
        description: t("sessionModal.closeOnly.description", {
          defaultValue: "Votre session Vigilab va bientôt expirer.",
        }),
        timer: t("sessionModal.closeOnly.closingIn", { defaultValue: "Fermeture dans :" }),
        leave: t("sessionModal.closeOnly.close", { defaultValue: "Fermer" }),
      }
    : {
        title: t("sessionModal.title", { defaultValue: "Session Expiring" }),
        description: t("sessionModal.description", {
          defaultValue: "You've been inactive. We'll log you out shortly.",
        }),
        timer: t("sessionModal.autoLogoutIn", { defaultValue: "Auto logout in:" }),
        leave: t("sessionModal.logout", { defaultValue: "Logout" }),
      };

  return (
    <Portal>
      {/* Backdrop with light blur */}
      <Box
        position="fixed"
        inset={0}
        zIndex={2000}
        bg="rgba(0, 0, 0, 0.08)"
        backdropFilter="blur(10px)"
        display="flex"
        alignItems="center"
        justifyContent="center"
        px="20px"
      >
        {/* Main Modal Card */}
        <Box
          width="520px"
          bg="white"
          borderRadius="40px"
          boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.15)"
          p="48px"
          textAlign="center"
        >
          <VStack spacing="32px">
            
            {/* favicon.png Logo Section */}
            <Center>
              <Circle size="90px" bg="#F1F5FD" overflow="hidden">
                <Image 
                  src="/favicon.png" 
                  alt={t("aria.logo", { defaultValue: "Logo" })} 
                  boxSize="50px" 
                  objectFit="contain"
                  fallbackSrc="https://via.placeholder.com/50"  
                />
              </Circle>
            </Center>

            {/* Typography Section */}
            <VStack spacing="12px">
              <Text
                fontSize="32px"
                fontWeight="800"
                color="#2D3553"
                letterSpacing="-0.5px"
              >
                {texts.title}
              </Text>
              <Text
                fontSize="18px"
                fontWeight="500"
                color="#718096"
              >
                {texts.description}
              </Text>
            </VStack>

            {/* Timer Section - Matching the design you liked */}
            <HStack spacing={3} justify="center">
               <Text fontWeight="600" color="#2D3553" fontSize="18px">
                 {texts.timer}
               </Text>
               <Box 
                 bg="#2e3e7a" 
                 color="white" 
                 px={12} 
                 py={1.5} 
                 borderRadius="10px" 
                 fontWeight="700"
                 fontSize="18px"
               >
                 {remainingSeconds}s
               </Box>
            </HStack>

            <Box w="100%" h="1px" bg="#EDF2F7" />

            {/* Actions */}
            <HStack w="100%" justify="space-between" spacing={4}>
              <ActionButton
                label={texts.leave}
                icon={LogOut}
                variant="outline"
                onClick={onLogout}
                
                h="50px"
                px="30px"
                borderRadius="18px"
                _hover={{ bg: "#F7FAFC", borderColor: "#CBD5E0" }}
              />

              <ActionButton
                label={t("sessionModal.stayLoggedIn", { defaultValue: "Stay Logged In" })}
                icon={ArrowRight}
                iconPosition="right"
                onClick={onContinue}
                variant="primary"
                h="50px"
                flex={1}
                borderRadius="18px"
                fontSize="18px"
                bg="#2e3e7a" // Your theme color
                _hover={{ bg: "#1F2D5A" }}
              />
            </HStack>
          </VStack>
        </Box>
      </Box>
    </Portal>
  );
}
